import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { ParishWebsiteBadge } from "./components/ParishWebsiteBadge";
import { AuthProvider, useAuth } from "./lib/auth";
import { I18nProvider } from "./lib/i18n";
import Shell from "./components/Shell";
import Login from "./pages/Login";
import Home from "./pages/Home";
import CollectionForm from "./pages/CollectionForm";
import CollectionDetail from "./pages/CollectionDetail";
import Collections from "./pages/Collections";
import Expenses from "./pages/Expenses";
import Remittances from "./pages/Remittances";
import Dues from "./pages/Dues";
import Special from "./pages/Special";
import Dashboard from "./pages/Dashboard";
import Reports from "./pages/Reports";
import Corrections from "./pages/Corrections";
import SearchPage from "./pages/Search";
import Admin from "./pages/Admin";
import PublicSummary from "./pages/PublicSummary";
import VerifyReport from "./pages/VerifyReport";
import ChangePassword from "./pages/ChangePassword";
import Roster from "./pages/Roster";
import Register from "./pages/Register";
import { AuthComplete, ForgotPassword, RegistrationStatus } from "./pages/AuthPages";
import MyAccount, { AddEmail } from "./pages/MyAccount";
import { errMsg } from "./lib/api";

function GoogleCallback() {
  const { loginGoogle } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(loc.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    loginGoogle(sid).then(() => nav("/", { replace: true })).catch((e) => nav("/login", { replace: true, state: { err: errMsg(e) } }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#5C4033]" /></div>;
}

function Protected({ children, perm }) {
  const { user, can, meta } = useAuth();
  if (user === null || (user && !meta)) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#5C4033]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (perm && !perm.some((p) => can(p))) return <Navigate to="/" replace />;
  if (user.must_change_password) return <Shell><ChangePassword /></Shell>;
  if (user.must_add_email) return <Shell><AddEmail /></Shell>;
  return <Shell>{children}</Shell>;
}

function AppRoutes() {
  const P = (el, perm) => <Protected perm={perm}>{el}</Protected>;
  const loc = useLocation();
  if (loc.hash?.includes("session_id=")) return <GoogleCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/complete" element={<AuthComplete />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/registration-status" element={<RegistrationStatus />} />
      <Route path="/account" element={P(<MyAccount />)} />
      <Route path="/public" element={<PublicSummary />} />
      <Route path="/verify/:ref" element={<VerifyReport />} />
      <Route path="/" element={P(<Home />)} />
      <Route path="/collections" element={P(<Collections />)} />
      <Route path="/collections/new" element={P(<CollectionForm />, ["collection:write"])} />
      <Route path="/collections/:id/edit" element={P(<CollectionForm />, ["collection:write"])} />
      <Route path="/collections/:id" element={P(<CollectionDetail />)} />
      <Route path="/expenses" element={P(<Expenses />)} />
      <Route path="/remittances" element={P(<Remittances />)} />
      <Route path="/dues" element={P(<Dues />)} />
      <Route path="/special" element={P(<Special />)} />
      <Route path="/dashboard" element={P(<Dashboard />)} />
      <Route path="/reports" element={P(<Reports />)} />
      <Route path="/corrections" element={P(<Corrections />)} />
      <Route path="/roster" element={P(<Roster />)} />
      <Route path="/search" element={P(<SearchPage />)} />
      <Route path="/change-password" element={P(<ChangePassword />)} />
      <Route path="/admin" element={P(<Admin />, ["config", "users", "audit:read"])} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
        <ParishWebsiteBadge />
      </AuthProvider>
    </I18nProvider>
  );
}
